import {ERROR_STATUS_LABEL, StepStatus} from './constants';
import type {StepItem} from './types';

/**
 * A hidden label leaves the step button with no accessible name, so one has to be
 * supplied. Only a plain-string label can serve as its own, which is why a step
 * built from markup has to carry `ariaLabel`.
 *
 * An `aria-label` replaces the button's contents outright, so the hidden error
 * word rendered alongside the visible label cannot reach this mode — the status
 * has to be folded into the name here instead.
 */
export const getStepAriaLabel = (
  step: StepItem,
  isError = false,
): string | undefined => {
  const name =
    step.ariaLabel ?? (typeof step.label === 'string' ? step.label : undefined);

  if (!name) return undefined;

  return isError ? `${name} ${ERROR_STATUS_LABEL}` : name;
};

/**
 * A markup label cannot identify its step, so `id` is the only stable key such a
 * step can have. Index is the last resort — it reorders with the list.
 */
export const getStepKey = (step: StepItem, index: number): string | number =>
  step.id ?? (typeof step.label === 'string' ? step.label : index);

export const getStepStatus = (
  index: number,
  activeStep: number | undefined,
  override?: StepStatus,
): StepStatus => {
  if (override) return override;
  if (activeStep === undefined) return StepStatus.UPCOMING;
  if (index < activeStep) return StepStatus.COMPLETED;
  if (index === activeStep) return StepStatus.ACTIVE;

  return StepStatus.UPCOMING;
};
